// Edit-on-Active form. Per api-design §3 PATCH /v1/agreements/{id} the
// mutable field set depends on (status × role × ownership):
//   - identity / type / status: never editable here
//   - start/end/total/estimated/description: editable per state
//   - owner_person_id: Admin only
// The drawer renders only mutable fields for the (status, role) pair.

import { useEffect, useMemo } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agreementsApi, peopleApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import {
  isAdmin,
  isOwnerOf,
} from '@/auth/permissions';
import type { Agreement, Principal } from '@/types/domain';
import type { AgreementPatchBody } from '@/api/endpoints';

interface Props {
  open: boolean;
  onClose: () => void;
  agreement: Agreement;
  principal: Principal | null;
}

interface FormShape {
  start_date: string;
  end_date: string;
  total_value: string;
  estimated_value: string;
  description: string;
  owner_person_id: string;
}

const KNOWN: string[] = [
  'start_date',
  'end_date',
  'total_value',
  'estimated_value',
  'description',
  'owner_person_id',
];

export function AgreementEditDrawer({ open, onClose, agreement, principal }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const isFP = agreement.type === 'fixed_price';
  const isTM = agreement.type === 't_and_m';
  const canEditOwner = isAdmin(principal);
  const editableThisRow =
    agreement.status !== 'completed' &&
    agreement.status !== 'archived' &&
    (isAdmin(principal) || (principal ? isOwnerOf(principal, agreement) : false));

  const owners = useQuery({
    queryKey: qk.people({ role: ['admin', 'practice_lead'] }),
    queryFn: () => peopleApi.list({ role: ['admin', 'practice_lead'], limit: 100 }),
    staleTime: 60_000,
    enabled: open && canEditOwner,
  });

  const defaults = useMemo<FormShape>(
    () => ({
      start_date: agreement.start_date,
      end_date: agreement.end_date ?? '',
      total_value: agreement.total_value ?? '',
      estimated_value: agreement.estimated_value ?? '',
      description: agreement.description ?? '',
      owner_person_id: agreement.owner_person_id,
    }),
    [agreement],
  );

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<FormShape>({ defaultValues: defaults });

  useEffect(() => {
    if (open) reset(defaults);
  }, [open, reset, defaults]);

  // Browser reload-warning safety net per G-FE-1.
  useDirtyFormReloadGuard(open && isDirty);

  const m = useMutation({
    mutationFn: (body: AgreementPatchBody) => agreementsApi.update(agreement.id, body),
  });

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    // JSON Merge Patch: only send changed fields.
    const body: AgreementPatchBody = {};
    if (dirtyFields.start_date) body.start_date = values.start_date;
    if (dirtyFields.end_date)
      body.end_date = values.end_date ? values.end_date : null;
    if (dirtyFields.description)
      body.description = values.description ? values.description : null;
    if (isFP && dirtyFields.total_value) body.total_value = values.total_value;
    if (isTM && dirtyFields.estimated_value)
      body.estimated_value = values.estimated_value || null;
    if (canEditOwner && dirtyFields.owner_person_id)
      body.owner_person_id = values.owner_person_id;

    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }

    try {
      await m.mutateAsync(body);
      void qc.invalidateQueries({ queryKey: qk.agreement(agreement.id) });
      void qc.invalidateQueries({ queryKey: ['agreements', 'list'] });
      toast.show({ severity: 'success', message: 'Agreement saved.' });
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (mapped.formMessage)
        toast.show({ severity: 'error', message: mapped.formMessage });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit agreement"
      role="dialog"
      variant="drawer"
      canClose={() => {
        if (!isDirty) return true;
        return window.confirm('Discard unsaved changes?');
      }}
    >
      {!editableThisRow ? (
        <p>This agreement isn't editable in its current state.</p>
      ) : (
        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <div className="form-field">
            <label htmlFor="ed-start_date">Start date</label>
            <input
              id="ed-start_date"
              type="date"
              disabled={isSubmitting}
              {...register('start_date', { required: 'Start date is required.' })}
            />
            {errors.start_date ? (
              <span className="form-error">{errors.start_date.message}</span>
            ) : null}
          </div>
          <div className="form-field">
            <label htmlFor="ed-end_date">End date</label>
            <input
              id="ed-end_date"
              type="date"
              disabled={isSubmitting}
              {...register('end_date', {
                validate: (v) => {
                  if (!v) return true;
                  const start = watch('start_date');
                  if (start && v < start)
                    return 'End date must be on or after the start date.';
                  return true;
                },
              })}
            />
            {errors.end_date ? (
              <span className="form-error">{errors.end_date.message}</span>
            ) : null}
          </div>
          {isFP ? (
            <div className="form-field">
              <label htmlFor="ed-total_value">Total value (USD)</label>
              <input
                id="ed-total_value"
                inputMode="decimal"
                disabled={isSubmitting}
                {...register('total_value', {
                  required: 'Total value is required for Fixed-Price.',
                  pattern: {
                    value: /^\d+(?:\.\d{1,2})?$/,
                    message: 'Enter dollars and cents.',
                  },
                })}
              />
              {errors.total_value ? (
                <span className="form-error">{errors.total_value.message}</span>
              ) : null}
            </div>
          ) : null}
          {isTM ? (
            <div className="form-field">
              <label htmlFor="ed-estimated_value">Estimated value (USD)</label>
              <input
                id="ed-estimated_value"
                inputMode="decimal"
                disabled={isSubmitting}
                {...register('estimated_value', {
                  pattern: {
                    value: /^\d+(?:\.\d{1,2})?$/,
                    message: 'Enter dollars and cents.',
                  },
                })}
              />
              {errors.estimated_value ? (
                <span className="form-error">{errors.estimated_value.message}</span>
              ) : null}
            </div>
          ) : null}
          <div className="form-field">
            <label htmlFor="ed-description">Description</label>
            <textarea
              id="ed-description"
              rows={4}
              disabled={isSubmitting}
              {...register('description')}
            />
          </div>
          {canEditOwner ? (
            <div className="form-field">
              <label htmlFor="ed-owner">Owner</label>
              <select
                id="ed-owner"
                disabled={isSubmitting || owners.isPending}
                {...register('owner_person_id')}
              >
                {owners.data?.data.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.email})
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              aria-busy={isSubmitting ? 'true' : 'false'}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
